/**
 * Home — fase "antes": contagem regressiva e o checklist de preparação.
 *
 * Home v2 (PO, 19/09/2026 — protótipo A escolhido): a Home antes da viagem
 * **é** a lista do que falta. Um único card, "Preparação — N de 5", com uma
 * linha por frente, estado real e a ação que tira a linha do vermelho:
 *
 *   Roteiro     — dias com programação (dados do roteiro);
 *   Grupo       — pessoas confirmadas (membros); sozinho → "convide quem vai";
 *   Bagagem     — itens na lista + os ESSENCIAIS que ainda não aparecem
 *                 (regras no cliente, `bagagem/essenciais.ts`);
 *   Hospedagem  — cadastrada em Configurações da viagem;
 *   Emergência  — o MEU cartão preenchido (telefone ou contato).
 *
 * Saiu daqui (redundante com a barra): o grid Participantes / Roteiro /
 * Financeiro / Mais. Tudo que aparece sai de dado real; nada é inventado.
 */
import { baggageProgress, featureEnabled, pluralize, type TripDates } from '@nv/core';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermissao, useTrip } from '../../../trip/TripContext';
import { Icon, type IconName } from '../../../ui/icons';
import { Card, ProgressBar } from '../../../ui/primitives';
import { essenciaisFaltando, foraDoFusoDoAparelho } from '../../bagagem/essenciais';
import { useBagagem } from '../../bagagem/store';
import { useMinhaEmergencia } from '../../emergencia/api';
import { ConviteDeFamiliaNaHome } from '../../grupo/ConviteDeFamiliaNaHome';
import { PedidosDeSugestaoNaHome } from '../../sugestoes/PedidosNaHome';
import { rotaDeConvidar } from '../../grupo/convites';
import { HospedagemNaHome } from '../../hospedagem/HospedagemNaHome';
import type { HomeData } from '../data';
import type { TripHeader } from '../model';
import { ContagemRegressiva } from '../ContagemRegressiva';

interface Frente {
  id: string;
  icone: IconName;
  titulo: string;
  detalhe: string;
  pronta: boolean;
  acao: string;
  para: string;
}

export function BeforePhase({ header, data, viagem, agora }: { header: TripHeader; data: HomeData; viagem: TripDates | null; agora: Date }) {
  const navigate = useNavigate();
  const { trip, tripId, meuMemberId, members } = useTrip();
  const { preparation } = data;
  const podeConvidar = usePermissao('manage_members');

  // Bagagem: a minha lista (privada + itens da viagem a meu cargo).
  const bagagemLigada = featureEnabled(trip.features, 'bagagem');
  const { estado, carregar } = useBagagem();
  useEffect(() => {
    if (bagagemLigada) void carregar(tripId);
  }, [bagagemLigada, tripId, carregar]);
  const meus = (estado?.items ?? []).filter((i) => i.scope === 'private' || (i.scope === 'trip' && i.assigneeMemberId === meuMemberId));
  const mala = baggageProgress(meus);
  const faltam = essenciaisFaltando(
    meus.map((i) => i.title),
    { foraDoFuso: foraDoFusoDoAparelho(trip.timezone), dias: header.durationInDays },
  );

  const emergencia = useMinhaEmergencia();
  const cartaoPreenchido = Boolean(emergencia.data && (emergencia.data.phone || emergencia.data.contactPhone || emergencia.data.contactName));
  const temHospedagem = Boolean(trip.accommodationName || trip.accommodationAddress);
  const sozinho = members.length < 2;

  const frentes: Frente[] = [
    {
      id: 'roteiro',
      icone: 'roteiro',
      titulo: 'Roteiro',
      detalhe:
        preparation.daysPlanned === 0
          ? `Nenhum dos ${pluralize(preparation.totalDays, 'dia', 'dias')} tem programação`
          : `${preparation.daysPlanned} de ${pluralize(preparation.totalDays, 'dia', 'dias')} com programação`,
      pronta: preparation.totalDays > 0 && preparation.daysPlanned === preparation.totalDays,
      acao: preparation.daysPlanned === 0 ? 'Montar' : 'Continuar',
      para: '/roteiro',
    },
    {
      id: 'grupo',
      icone: 'pessoas',
      titulo: 'Grupo',
      detalhe: sozinho ? 'Só você por enquanto — convide quem vai' : pluralize(members.length, 'pessoa confirmada', 'pessoas confirmadas'),
      pronta: !sozinho,
      acao: podeConvidar ? 'Convidar' : 'Ver',
      // Sozinho, a ação é convidar; com gente, é ver o grupo (o convite continua no cabeçalho de lá).
      para: sozinho ? rotaDeConvidar(podeConvidar) : '/grupo',
    },
    ...(bagagemLigada
      ? [
          {
            id: 'bagagem',
            icone: 'mala' as const,
            titulo: 'Bagagem',
            detalhe:
              faltam.length > 0
                ? `Não vi na lista: ${faltam
                    .slice(0, 3)
                    .map((e) => e.rotulo.toLowerCase())
                    .join(', ')}${faltam.length > 3 ? ` +${faltam.length - 3}` : ''}`
                : mala.total === 0
                  ? 'Nenhum item na lista'
                  : mala.done
                    ? `Mala pronta · ${pluralize(mala.total, 'item', 'itens')}`
                    : `${mala.packed} de ${mala.total} na mala`,
            pronta: mala.total > 0 && faltam.length === 0,
            acao: mala.total === 0 ? 'Montar' : 'Continuar',
            para: '/bagagem',
          },
        ]
      : []),
    {
      id: 'hospedagem',
      icone: 'hospedagem',
      titulo: 'Hospedagem',
      detalhe: temHospedagem ? (trip.accommodationName ?? 'Cadastrada') : 'Onde o grupo vai ficar?',
      pronta: temHospedagem,
      acao: 'Cadastrar',
      para: '/viagem/configuracoes',
    },
    {
      id: 'emergencia',
      icone: 'aviso',
      titulo: 'Cartão de emergência',
      detalhe: cartaoPreenchido ? 'O seu está preenchido' : 'Seu telefone e um contato, para quem viaja com você',
      pronta: cartaoPreenchido,
      acao: 'Preencher',
      para: '/emergencia',
    },
  ];
  const prontas = frentes.filter((f) => f.pronta).length;

  return (
    <>
      <div className="home-faixa" data-testid="home-faixa">
        <ConviteDeFamiliaNaHome />
        <PedidosDeSugestaoNaHome />
      </div>
      {viagem && <ContagemRegressiva viagem={viagem} agora={agora} hora={data.horaDaPartida} />}

      <Card data-testid="home-preparacao" className="home-prep" lista>
        <div className="home-prep__head">
          <span className="home-prep__title">Preparação</span>
          <span className="home-prep__percent" data-testid="home-preparacao-contagem">
            {prontas} de {frentes.length}
          </span>
        </div>
        <ProgressBar percent={Math.round((prontas / frentes.length) * 100)} label="Preparação" />
        <div className="prep">
          {frentes.map((f) => (
            <button type="button" key={f.id} className="prep__item" onClick={() => navigate(f.para)} data-testid={`prep-${f.id}`} data-pronta={f.pronta ? '1' : '0'}>
              <span className={f.pronta ? 'prep__estado prep__estado--ok' : 'prep__estado'} aria-hidden="true">
                <Icon name={f.pronta ? 'concluir' : f.icone} size={f.pronta ? 18 : 20} />
              </span>
              <span className="prep__texto">
                <span className="prep__titulo">{f.titulo}</span>
                <span className="prep__detalhe">{f.detalhe}</span>
              </span>
              {f.pronta ? <Icon name="avancar" size={16} /> : <span className="nv-btn nv-btn--chip prep__acao">{f.acao}</span>}
            </button>
          ))}
        </div>
      </Card>

      <HospedagemNaHome />
    </>
  );
}
